import { css } from '@emotion/react';
import styled from 'types/theme.types';
import { Tree, Input } from 'antd';
import Button from 'components/common/button';
import { Link } from 'react-router-dom';
import { legacyOnly, newDesignOnly, type Env0Theme } from 'constants/themes.constants';

export const Container = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 400;
  opacity: 0.8;
  padding: 6px 0 4px 0;

  ${legacyOnly(
    theme => css`
      color: ${theme.textBaseGray};
    `
  )}

  ${newDesignOnly(css`
    color: var(--text-secondary);
  `)}
`;

/**
 * Figma's Button at Tertiary, sized for the top bar. The label colour is deliberately unset so it comes
 * from the Button wrapper, keeping this and the Docs button matching by construction. Doubled because
 * antd states the same geometry on `.ant-btn`. New-design only; legacy uses TriggerContainer.
 */
export const TriggerButton = styled(Button)`
  && {
    display: flex;
    align-items: center;
    gap: var(--spacing-xxxxs);
    height: 32px;
    padding: 0 var(--padding-md);
    border-radius: var(--border-radius-100);
    font-size: var(--font-size-labels-label-md);
  }
`;

// Switch-off only: the new design renders TriggerButton above.
export const TriggerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  ${legacyOnly(css`
    padding: 8px 12px;
    border-radius: 6px;
  `)}
  transition: background-color 0.2s ease;

  ${legacyOnly(
    theme => css`
      &:hover {
        background-color: ${theme.popoverTreeSelectorHover};
      }
    `
  )}
`;

export const IconContainer = styled.div`
  ${legacyOnly(css`
    font-size: 8px;
  `)}
  ${newDesignOnly(css`
    font-size: 16px;
  `)}
  display: flex;
  flex-direction: column;
  gap: 1px;
  /* Stays here: moving it reorders the emitted CSS and fails the parity gate. */
  ${legacyOnly(css`
    padding: 4px;
  `)}
  border-radius: 4px;
  transition: background-color 0.2s ease;

  ${legacyOnly(
    theme => css`
      color: ${theme.textColor};

      &:hover {
        background-color: ${theme.sideMenuBackground};
      }
    `
  )}

  /* No hover on the new side: the chevron is a bare icon inside the Button, which lights up itself. */
  ${newDesignOnly(css`
    color: var(--text-heading);
  `)}
`;

export const LabelContainer = styled.div`
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;

  ${legacyOnly(
    theme => css`
      color: ${theme.textColor};
    `
  )}

  ${newDesignOnly(css`
    color: var(--text-heading);
  `)}
`;

export const FooterButton = styled(Button)`
  ${legacyOnly(
    () => css`
      text-align: center;
      height: auto;
      font-weight: 500;
    `
  )}
`;

// TreeSelector styled components
export const TreeSelectorContainer = styled.div<{ $popoverWidthPx?: number }>`
  --tree-selector-width: ${({ $popoverWidthPx }) => ($popoverWidthPx ? `${$popoverWidthPx}px` : '280px')};
  --add-button-width: 18px;
  --tree-node-gap: 8px;
  --switcher-width: 56px;
  width: var(--tree-selector-width);
  padding: 8px;
  display: flex;
  flex-direction: column;

  ${legacyOnly(
    theme => css`
      background: ${theme.primaryWhite};
    `
  )}

  ${newDesignOnly(css`
    background: var(--surface-main-cards);
  `)}
`;

export const TREE_VIEWPORT_HEIGHT_PX = 390;
export const TREE_ITEM_HEIGHT_PX = 32;

// What is left unbranched is structural: `genTreeStyle` derives none of it the way this popover needs. The
// row colours and radius now come from the `Tree` bridge section instead, except the virtual scrollbar
// thumb — `genTreeStyle` reads no alias that reaches `.ant-tree-list-scrollbar-thumb`.
export const StyledTree = styled(Tree)`
  flex: 1;
  min-height: 0;

  .ant-tree-list-holder {
    overflow-x: hidden;
  }

  .ant-tree-node-content-wrapper {
    padding: 6px 10px;

    &:hover button {
      opacity: 1;
      visibility: visible;
    }
  }

  .ant-tree-title {
    width: 100%;
  }

  .ant-tree-treenode {
    display: flex;
    align-items: flex-start;
    margin: 0;
    padding: 2px 0;
  }

  .ant-tree-switcher {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ant-tree-indent-unit {
    width: 16px;
  }

  ${legacyOnly(
    theme => css`
      .ant-tree-list-scrollbar-thumb {
        background: ${theme.textGray} !important;
      }

      .ant-tree-list {
        background: ${theme.primaryWhite};
      }

      .ant-tree-node-content-wrapper {
        border-radius: 6px;
        transition: background-color 0.2s ease;

        &:hover {
          background-color: ${theme.popoverTreeSelectorHover};
        }

        &.ant-tree-node-selected {
          background-color: ${theme.popoverTreeSelectorHover};
        }
      }
    `
  )}

  ${newDesignOnly(css`
    .ant-tree-list-scrollbar-thumb {
      background: var(--surface-scrollbar-thumb) !important;
    }
  `)}
`;

export const SearchContainer = styled.div`
  margin-bottom: 6px;
`;

export const StyledInput = styled(Input)`
  ${legacyOnly(css`
    border-radius: 6px;
  `)}
`;

export const FooterContainer = styled.div`
  margin-top: 8px;
  padding-top: 12px;

  ${legacyOnly(
    theme => css`
      border-top: 1px solid ${theme.separatorGray};
    `
  )}

  ${newDesignOnly(css`
    border-top: 1px solid var(--border-primary);
  `)}
`;

const TreeNodeContainerBase = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--tree-node-gap);
  width: 100%;
  font-size: 13px;
`;

const TreeNodeContainerLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--tree-node-gap);
  width: 100%;
  font-size: 13px;
  color: inherit;
  text-decoration: none;

  &:hover {
    color: inherit;
    text-decoration: none;
  }

  &:focus {
    color: inherit;
    text-decoration: none;
  }

  &:visited {
    color: inherit;
  }
`;

export { TreeNodeContainerBase, TreeNodeContainerLink };

export const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--add-button-width);
  height: var(--add-button-width);
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  align-self: center;
  opacity: 0;
  visibility: hidden;

  &:active {
    transform: scale(0.95);
  }

  ${legacyOnly(
    theme => css`
      background: ${theme.whiteColor};
      color: ${theme.textGray};
    `
  )}

  ${newDesignOnly(css`
    background: var(--surface-main-cards);
    color: var(--text-secondary);
  `)}
`;

export const TreeNodeTitle = styled.div`
  min-width: 0;
  flex: 1;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.2;
`;

export const SkeletonContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;

  // antd sets width, min-width and height on the same three classes, so all three must be named or its
  // rule wins the tie and the skeleton renders at 160x32.
  ${legacyOnly(css`
    // Switch-off keeps the inline style's own reach: antd's min-width beat the 60px width there, so the
    // skeleton rendered 160 wide, and naming min-width here would shrink it.
    .ant-skeleton.ant-skeleton-element .ant-skeleton-input {
      width: 60px;
      height: 16px;
    }
  `)}

  ${newDesignOnly(css`
    .ant-skeleton.ant-skeleton-element .ant-skeleton-input {
      width: 60px;
      min-width: 60px;
      height: 16px;
    }
  `)}
`;

/**
 * antd hardcodes `.ant-popover-inner`'s padding inside its style hook and `popoverPadding` is not in
 * `Popover`'s ComponentToken, so there is no bridge key and the rule needs a class of its own. Branched by
 * hand rather than with `legacyOnly` / `newDesignOnly`, which take styled-component props a `<Global>`
 * block has none of — same form as `getGlobalStyles` in `antd-wrapper.tsx`.
 *
 * `overlayClassName` lands on the `.ant-popover` root, so naming both classes makes this three where
 * antd's `.ant-popover .ant-popover-inner` is two. On a tie it would win by insertion order instead, and
 * cssinjs re-inserts its style tags on a live theme switch while this block does not.
 */
export const POPOVER_OVERLAY_CLASS = 'popover-tree-selector-overlay';

export const popoverOverlayStyles = (theme: Env0Theme) =>
  theme.isNewDesign
    ? css`
        .ant-popover.${POPOVER_OVERLAY_CLASS} .ant-popover-inner {
          background: var(--surface-main-cards);
          padding: 0;
        }
      `
    : css`
        .ant-popover.${POPOVER_OVERLAY_CLASS} .ant-popover-inner {
          background: ${theme.primaryGray};
          padding: 0;
        }
      `;

export const NoDataContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  gap: 8px;

  .anticon {
    font-size: 16px;
  }

  ${legacyOnly(
    theme => css`
      color: ${theme.textGray};

      .anticon {
        color: ${theme.sideMenuBackground};
      }
    `
  )}

  ${newDesignOnly(css`
    color: var(--text-secondary);

    .anticon {
      color: var(--icon-secondary);
    }
  `)}
`;
